import {getIframeVideo, scrollTo, isMatchMedia} from '%helpers%/utils';

class Video {
  constructor(element) {
    if (!element) return;

    this.element = element;
    this.modal = null;
    this.modalEl = null;
    this.videoBlock =
      (this._getTarget() && document.querySelector(this._getTarget())) ||
      this.element.closest('.video-block, .img-block');
    this.playlist = this.element.closest('.widget-video_list');

    if (!this.element.classList.contains('active')) {
      if (this._shouldPlayInside()) {
        this._playInside();
      } else {
        this._showVideoModal();
      }
      if (this.playlist) {
        this.playlist.querySelector('.list-group-item.active').classList.remove('active');
        this.element.classList.add('active');
        if (!isMatchMedia('desktop'))
          scrollTo(
            this.videoBlock.offsetTop -
              ((document.querySelector('.page-header') &&
                document.querySelector('.page-header').offsetHeight) ||
                0) -
              16
          );
      }
    }
  }

  _showVideoModal() {
    this._showModal();
    this._setListeners();
  }

  _showModal() {
    const template = this._getModalNode();
    document.body.appendChild(template);

    this.modalEl = document.querySelector('.modal-video');
    this.modal = bootstrap.Modal.getOrCreateInstance(this.modalEl, {
      keyboard: true,
      backdrop: true,
    });

    const videoPreview = this.element.closest('.video-wrapper, .card-img-block');
    if (videoPreview) {
      this.modalEl.classList.add('invisible');

      const img = this.element.parentNode.querySelector('.img-block');
      const rect = img.getBoundingClientRect();
      const computedStyle = getComputedStyle(videoPreview);

      const clone = img.cloneNode(true);
      clone.classList.add('clone-preview');

      const top = rect.top + (rect.height - img.offsetHeight) / 2;
      const left = rect.left + (rect.width - img.offsetWidth) / 2;

      const duration = isMatchMedia('md') ? 1300 : 600;

      Object.assign(clone.style, {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${img.offsetWidth}px`,
        height: `${img.offsetHeight}px`,
        borderRadius: '10px',
        transition: `all ${duration}ms cubic-bezier(0.33, 0, 0.67, 1)`,
      });

      document.body.appendChild(clone);
      this.modal.show(this.element);

      requestAnimationFrame(() => {
        const videoBlock = this.modalEl.querySelector('.video-block');
        const targetRect = videoBlock.getBoundingClientRect();

        Object.assign(clone.style, {
          top: `${targetRect.top}px`,
          left: `${targetRect.left}px`,
          width: `${targetRect.width}px`,
          height: `${targetRect.height}px`,
          transform: 'none',
          borderRadius: this._getRatio() ? `${targetRect.borderRadius}px` : 0,
        });

        setTimeout(() => {
          this.modalEl.classList.remove('invisible');
          setTimeout(() => clone.remove(), 150);
        }, duration);
      });
    } else {
      this.modal.show(this.element);
    }
  }

  _setListeners() {
    this.modalEl.addEventListener('hidden.bs.modal', () => {
      this.modal.dispose();
      this.modalEl.remove();

      this.modal = null;
      this.modalEl = null;
      this.element = null;
    });
  }

  _getSrc() {
    return this.element.dataset.src;
  }

  _getIframeVideo() {
    return getIframeVideo(this._getSrc(), {
      autoplay: 1,
      mute: 1,
      fullscreen: 1,
      modestbranding: 0,
      rel: 0,
    });
  }

  _getTarget() {
    return this.element.dataset.bsTarget || null;
  }

  _getRatio() {
    return this.element.dataset.ratio || null;
  }

  _getImgPreview() {
    return this.element.dataset.imgSrc || null;
  }

  _isLocalVideo() {
    return this.element.dataset.localVideo !== undefined;
  }

  _shouldPlayInside() {
    const {videoInside = null, bsToggle} = this.element.dataset;
    return videoInside || videoInside === 'videoInside' || bsToggle === 'video';
  }

  _getModalNode() {
    const template = document.createElement('div');
    const ratioClassName = this._getRatio() ? 'ratio-' + this._getRatio() : 'h-100';

    template.classList.add('modal', 'modal-video');
    template.classList.toggle('is-fullscreen', ratioClassName === 'h-100');
    template.setAttribute('tabindex', '-1');
    template.setAttribute('aria-hidden', 'true');
    template.innerHTML = `
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            <div class="modal-dialog ${ratioClassName === 'h-100' ? 'modal-fullscreen' : 'modal-dialog-centered modal-xl'}">
               <div class="modal-content">
                    <div class="modal-body p-0">
                        <div class="video-block show ratio ${ratioClassName} bg-black"></div>
                    </div>
                </div>
            </div>`;

    const videoBlock = template.querySelector('.video-block');
    if (this._isLocalVideo()) {
      const videoElement = document.createElement('video');
      videoElement.setAttribute('controls', 'controls');
      videoElement.setAttribute('autoplay', 'autoplay');
      videoElement.innerHTML = `<source src="${this._getSrc()}" type="video/mp4">Your browser does not support the video tag.`;
      videoBlock.appendChild(videoElement);
    } else {
      const iframe = this._getIframeVideo();
      iframe.addEventListener('load', () => {
        iframe.classList.add('loaded');
      });
      videoBlock.appendChild(iframe);
    }

    return template;
  }

  _playInside() {
    if (!this.videoBlock) return;

    this._closeInlineVideo();
    this.videoBlock.classList.add('show');

    if (this._isLocalVideo()) {
      const videoElement = document.createElement('video');
      videoElement.classList = 'video';
      videoElement.setAttribute('controls', 'controls');
      videoElement.setAttribute('autoplay', 'autoplay');
      videoElement.innerHTML = `<source src="${this._getSrc()}" type="video/mp4">Your browser does not support the video tag.`;
      this.videoBlock.appendChild(videoElement);
    } else if (this._shouldPlayInside() && !this._isLocalVideo()) {
      this.videoBlock.appendChild(this._getIframeVideo());
    }

    if (!this.playlist) {
      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.classList.add('btn-close', 'btn-close-video');
      closeButton.setAttribute('aria-label', 'Close');
      closeButton.innerHTML = `
            <svg class="svg-icon">
                <use xlink:href="../images/spritemap/spritemap.svg#icon-close"/>
            </svg>`;
      closeButton.addEventListener('click', () => this._closeInlineVideo());
      this.videoBlock.appendChild(closeButton);
    }
  }

  _closeInlineVideo() {
    this.videoBlock.classList.remove('show');
    const video = this.videoBlock.querySelector('video, iframe');
    const btnClose = this.videoBlock.querySelector('.btn-close-video');
    if (video) video.remove();
    if (btnClose) btnClose.remove();
  }
}

export default Video;
